'use client'

import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Container } from '@quark/layout'
import { Text } from '@quark/typography'
import { VideoPlayer } from '@quark/video'
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import PropertyControlledComponent from '@components/PropertyControlledComponent/PropertyControlledComponent'
import { TrustProps } from '@interfaces/home'
import { createScrollHandler } from '@utils/mobileScrollUtils'

import styles from './videoPlayer.module.scss'

export type TrustHomeVideoPlayersProps = {
  videoPlayerData?: TrustProps['videoPlayerData']
  containerType?: 'fluid' | 'root'
}

const TrustHomeVideoPlayers = ({ videoPlayerData, containerType = 'root' }: TrustHomeVideoPlayersProps) => {
  const [activeDotIndex, setActiveDotIndex] = useState(0)
  const cardRef = useRef<HTMLDivElement>(null)
  const videoCardRefs = useRef<Array<HTMLDivElement | null>>([])
  const videoRefs = useRef<Array<HTMLVideoElement | null>>([])

  const videoPlayerDataArray = useMemo(
    () => (Array.isArray(videoPlayerData) ? videoPlayerData : videoPlayerData ? [videoPlayerData] : []),
    [videoPlayerData]
  )

  const resetInactiveVideos = (activeIndex: number) => {
    videoRefs.current.forEach((videoRef, index) => {
      const video = videoRef
      if (!video) return
      if (index !== activeIndex) {
        video.pause()
        video.currentTime = 0
        video.removeAttribute('src')
        video.load()
      }
    })
  }

  useEffect(() => {
    if (!videoPlayerDataArray?.length) return

    const handleScroll = createScrollHandler(cardRef, videoCardRefs, (index: number) => {
      setActiveDotIndex(index)
      resetInactiveVideos(index)
    })

    cardRef?.current?.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()

    return () => {
      cardRef?.current?.removeEventListener('scroll', handleScroll)
    }
  }, [videoPlayerDataArray])

  const handleVideoClick = async (eventProperties: {
    eventName?: string
    customProperties?: Record<string, unknown>
  }) => {
    await trackEvent(eventProperties?.eventName ?? 'tab_video_interaction', {
      page_type: 'acko_home_page',
      ...eventProperties?.customProperties,
    })
  }

  if (!videoPlayerDataArray.length) {
    return null
  }

  const type = containerType

  return (
    <>
      <Container type={type}>
        <div
          className={`${styles.videoRow} ${videoPlayerDataArray.length === 1 ? styles.singleVideo : ''}`}
          ref={cardRef}>
          {videoPlayerDataArray.map((item, idx) => (
            <div
              key={`${idx}-${activeDotIndex === idx ? 'active' : 'inactive'}`}
              className={styles.videoCard}
              ref={(el: HTMLDivElement | null) => {
                videoCardRefs.current[idx] = el
                videoRefs.current[idx] = el?.querySelector('video') ?? null
              }}>
              <Container
                borderRadius={{ lg: 'radius-6xl', md: 'radius-5xl', sm: 'radius-4xl' }}
                overflow="hidden"
                type={type}>
                <VideoPlayer
                  heading={
                    // Quark types heading as string but accepts ReactNode at runtime
                    (
                      <div className={styles.videoHeadingText}>
                        <Text
                          fontStyle={{
                            sm: 'um_text-sm/medium',
                            md: 'um_text-2xl/medium',
                            lg: 'um_text-3xl/medium',
                          }}
                          color="static/Primary-onDark"
                          as="div">
                          {`"${item?.heading ?? ''}"`}
                        </Text>
                      </div>
                    ) as unknown as string
                  }
                  onClick={() => {
                    void handleVideoClick(item?.videoEventProps || {})
                  }}
                  thumbnail={item.videoThumbnail ?? ''}
                  title={item?.authorData?.authorName ?? ''}
                  authorData={item?.authorData}
                  videoSrcSet={{
                    lg: item?.videoSrcSet?.lg ?? '',
                    md: item?.videoSrcSet?.md ?? '',
                    sm: item?.videoSrcSet?.sm ?? '',
                  }}
                />
              </Container>
            </div>
          ))}
        </div>
      </Container>
      <PropertyControlledComponent controllerProperty={videoPlayerDataArray.length > 1} fallback={null}>
        <div className={styles.dotsContainer}>
          {videoPlayerDataArray.map((_, index) => (
            <div key={index} className={`${styles.dot} ${activeDotIndex === index ? styles.activeDot : ''}`} />
          ))}
        </div>
      </PropertyControlledComponent>
    </>
  )
}

export default TrustHomeVideoPlayers
